// SPDX-License-Identifier: AGPL-3.0-or-later

import {PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import KeybindManager from '@app/features/app/keybindings/KeybindManager';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import styles from '@app/features/user/components/modals/tabs/KeybindsTab.module.css';
import NativePermission from '@app/features/voice/state/NativePermission';
import {openNativePermissionSettings, requestNativePermission} from '@app/features/voice/utils/NativePermissions';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';
export const InputMonitoringSection: React.FC = observer(() => {
	const {t} = useLingui();
	const [requesting, setRequesting] = useState(false);
	const status = NativePermission.inputMonitoringStatus;
	if (NativePermission.isLinuxWaylandDesktop) {
		return (
			<div className={styles.permissionCard}>
				<div className={styles.permissionText}>
					<div className={styles.permissionTitle}>
						<Trans>Wayland Global Shortcuts</Trans>
					</div>
					<p className={styles.permissionDescription}>
						<Trans>
							Toggle shortcuts use the desktop portal on Wayland. Hold-to-talk falls back to focused-window input if the
							compositor blocks global key release events.
						</Trans>
					</p>
				</div>
				<div className={styles.permissionActions}>
					<span className={styles.permissionStatus} data-status="checking">
						<Trans>Portal</Trans>
					</span>
				</div>
			</div>
		);
	}
	if (!NativePermission.shouldShowInputMonitoringBanner) {
		return null;
	}
	const handleRequest = async () => {
		setRequesting(true);
		const result = await requestNativePermission('input-monitoring');
		setRequesting(false);
		NativePermission.setInputMonitoringStatus(result);
		if (result === 'granted') {
			ToastCommands.createToast({type: 'success', children: t`Input Monitoring enabled`});
			await KeybindManager.reapplyGlobalShortcuts();
		} else if (result === 'denied') {
			await openNativePermissionSettings('input-monitoring');
			ToastCommands.error(t`Please enable ${PRODUCT_NAME} in System Settings → Privacy & Security → Input Monitoring.`);
		}
	};
	const statusLabel = (() => {
		if (status === 'denied') return t`Not granted`;
		if (status === 'not-determined') return t`Not granted`;
		return t`Granted`;
	})();
	return (
		<div className={styles.permissionCard}>
			<div className={styles.permissionText}>
				<div className={styles.permissionTitle}>
					<Trans>Input Monitoring</Trans>
				</div>
				<p className={styles.permissionDescription}>
					<Trans>
						{PRODUCT_NAME} needs Input Monitoring permission to keep push-to-talk and global shortcuts working while the
						window is in the background.
					</Trans>
				</p>
				{status === 'denied' ? (
					<p className={styles.permissionHelp}>
						<Trans>
							Click "Open Settings" to open System Settings, then enable {PRODUCT_NAME} in Input Monitoring.
						</Trans>
					</p>
				) : null}
			</div>
			<div className={styles.permissionActions}>
				<span className={styles.permissionStatus} data-status={status}>
					{statusLabel}
				</span>
				<Button variant="secondary" small={true} onClick={handleRequest} submitting={requesting}>
					{status === 'denied' ? t`Open Settings` : t`Enable`}
				</Button>
			</div>
		</div>
	);
});
